/**
 * Conversation Hub Screen
 * Landing screen for the Chat tab. Shows practice mode cards.
 * Header has three icon buttons: course switcher, autoplay toggle, history.
 * Tapping a card creates a session and navigates directly to Chat.
 */

import React, { useCallback, useState, useEffect } from 'react';
import {
  TouchableOpacity,
  View,
  ScrollView,
  Text,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useBottomTabBarHeight } from '@react-navigation/bottom-tabs';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import Ionicons from '../components/ui/Ionicons';

import { CourseSwitcherButton } from '../components/CourseSwitcherPill';
import { VocabProgressCard } from '../components/vocab/VocabProgressCard';
import { useConversationStore } from '../store/conversationStore';
import { selectStartNewSession, selectErrorInfo, selectClearError } from '../store/conversationSelectors';
import { useAudioMode } from '../hooks/useAudioMode';
import { useAIConsent } from '../hooks/useAIConsent';
import { AIConsentModal } from '../components/AIConsentModal';
import { useDisplayTheme } from '../hooks/useDisplayTheme';
/**
 * Hub modes shown to the user.
 *
 * Includes `roleplay` — a synthetic mode that does NOT correspond to a chat
 * flow. Tapping it navigates to the Role Play list screen instead of starting
 * a session. The actual `roleplay_generator` session is created from the
 * list screen's "+" affordance.
 */
export type HubMode =
  | 'pronunciation'
  | 'conversation'
  | 'grammar'
  | 'roleplay'
  | 'quick_question';

/** Chat-flow types that can directly spawn a session from the hub. */
type SessionFlowType = Exclude<HubMode, 'roleplay'>;

// ── Constants ─────────────────────────────────────────────────────────

const BASE_CIRCLE_SIZE = 80;
const BASE_CIRCLE_EMOJI = 40;
const BASE_BODY_SIZE = 14;
const BASE_BODY_LINE_HEIGHT = 22;
const BASE_CARD_EMOJI = 24;
const BASE_CARD_TITLE = 14;
const BASE_CARD_SUBTITLE = 12;
const HEADER_ICON_SIZE = 26;


interface ModeConfig {
  mode: HubMode;
  emoji: string;
}

const MODES: ModeConfig[] = [
  { mode: 'pronunciation', emoji: '🎙️' },
  { mode: 'conversation', emoji: '💬' },
  { mode: 'grammar', emoji: '📝' },
  { mode: 'roleplay', emoji: '🎭' },
  { mode: 'quick_question', emoji: '⚡' },
];

/** Translate mode title/subtitle using literal keys (required by literal-i18n-keys rule) */
function translateMode(t: TFunction, mode: HubMode): { title: string; subtitle: string } {
  switch (mode) {
    case 'pronunciation': return { title: t('hub.pronunciation.title'), subtitle: t('hub.pronunciation.subtitle') };
    case 'conversation': return { title: t('hub.conversation.title'), subtitle: t('hub.conversation.subtitle') };
    case 'grammar': return { title: t('hub.grammar.title'), subtitle: t('hub.grammar.subtitle') };
    case 'roleplay': return { title: t('hub.roleplay.title'), subtitle: t('hub.roleplay.subtitle') };
    case 'quick_question': return { title: t('hub.quickQuestion.title'), subtitle: t('hub.quickQuestion.subtitle') };
  }
}

// ── Mode Card ─────────────────────────────────────────────────────────

interface ModeCardProps {
  config: ModeConfig;
  onPress: (mode: HubMode) => void;
  disabled: boolean;
}

const ModeCard = React.memo(function ModeCard({ config, onPress, disabled }: ModeCardProps) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const { title, subtitle } = translateMode(t, config.mode);

  const handlePress = useCallback(() => {
    onPress(config.mode);
  }, [onPress, config.mode]);

  return (
    <TouchableOpacity
      testID={`hub-card-${config.mode}`}
      accessibilityLabel={title}
      accessibilityRole="button"
      onPress={handlePress}
      activeOpacity={0.7}
      disabled={disabled}
      style={[styles.cardTouchable, disabled && { opacity: 0.6 }]}
    >
      <View
        style={[
          styles.card,
          {
            backgroundColor: theme.card,
            borderColor: theme.border,
          },
        ]}
      >
        <Text
          allowFontScaling={false}
          accessible={false}
          importantForAccessibility="no"
          style={{ fontSize: BASE_CARD_EMOJI * fontScale, marginBottom: 8 }}
        >
          {config.emoji}
        </Text>
        <Text
          allowFontScaling={false}
          style={{
            fontSize: BASE_CARD_TITLE * fontScale,
            color: theme.text,
            fontWeight: '600',
            marginBottom: 4,
            ...ffs,
          }}
        >
          {title}
        </Text>
        <Text
          allowFontScaling={false}
          style={{
            fontSize: BASE_CARD_SUBTITLE * fontScale,
            color: theme.textSecondary,
            fontStyle: 'italic',
            ...ffs,
          }}
        >
          {subtitle}
        </Text>
      </View>
    </TouchableOpacity>
  );
});

// ── Screen ────────────────────────────────────────────────────────────

export function ConversationHubScreen() {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const navigation = useNavigation('ConversationHub');
  const startNewSession = useConversationStore(selectStartNewSession);
  const errorInfo = useConversationStore(selectErrorInfo);
  const clearError = useConversationStore(selectClearError);
  const { autoplay, toggle: toggleAutoplay } = useAudioMode();
  const { hasConsent, needsReConsent, grantConsent } = useAIConsent();

  const [isCreating, setIsCreating] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [pendingMode, setPendingMode] = useState<SessionFlowType | null>(null);

  // If server rejects with AI_CONSENT_REQUIRED, show consent modal
  useEffect(() => {
    if (errorInfo?.code === 'AI_CONSENT_REQUIRED') {
      clearError();
      setShowConsentModal(true);
    }
  }, [errorInfo?.code, clearError]);

  const createSessionAndNavigate = useCallback(
    async (mode: SessionFlowType) => {
      setIsCreating(true);
      try {
        // Map hub mode to chat flow type (roleplay handled separately upstream)
        await startNewSession(mode as 'pronunciation' | 'conversation' | 'grammar' | 'quick_question');
        const sessionId = useConversationStore.getState().currentSessionId;
        if (sessionId) {
          navigation.navigate('Chat', { sessionId });
        }
      } finally {
        setIsCreating(false);
      }
    },
    [startNewSession, navigation],
  );

  const handleSelectMode = useCallback(
    async (mode: HubMode) => {
      if (isCreating) return;

      // Role play → dedicated list screen (not a chat session)
      if (mode === 'roleplay') {
        navigation.navigate('RolePlayList');
        return;
      }

      // Gate: require consent before hitting the server
      if (!hasConsent || needsReConsent) {
        setPendingMode(mode);
        setShowConsentModal(true);
        return;
      }

      await createSessionAndNavigate(mode);
    },
    [isCreating, hasConsent, needsReConsent, createSessionAndNavigate, navigation],
  );

  const handleConsentAccept = useCallback(async () => {
    const granted = await grantConsent();
    // Always dismiss the modal so the AppBanner (rendered at
    // AppNavigator root level) isn't covered by the native Modal —
    // both on success (proceed to chat) and on failure (banner from
    // useAIConsent needs to be visible). The pending mode is only
    // consumed when consent actually granted; on failure we drop it
    // so the user has to deliberately re-tap the Hub card to retry
    // (avoids the doomed POST /sessions → 403 → re-open modal cycle).
    setShowConsentModal(false);
    const mode = pendingMode;
    setPendingMode(null);

    if (!granted) {
      // useAIConsent already surfaced the error banner. Don't
      // navigate — the next POST /sessions would 403 with
      // AI_CONSENT_REQUIRED and trigger the consent-modal-reopen
      // useEffect, which would cover the banner before the user
      // could read it.
      return;
    }

    if (mode) {
      await createSessionAndNavigate(mode);
    }
  }, [grantConsent, pendingMode, createSessionAndNavigate]);

  const handleConsentDecline = useCallback(() => {
    setShowConsentModal(false);
    setPendingMode(null);
  }, []);

  const handleHistory = useCallback(() => {
    navigation.navigate('ConversationList');
  }, [navigation]);

  const { top: paddingTop } = useSafeAreaInsets();
  const paddingBottom = useBottomTabBarHeight();
  const circleSize = Math.round(BASE_CIRCLE_SIZE * fontScale);
  const iconSize = Math.round(HEADER_ICON_SIZE * fontScale);

  return (
    <View style={{ flex: 1, backgroundColor: theme.background, paddingTop }}>{/* Wrapper needed for fixed header + scrollable content */}
      {/* Header */}
      <View style={styles.header}>
        <Text
          allowFontScaling={false}
          accessibilityRole="header"
          style={{
            fontSize: 28 * fontScale,
            fontWeight: 'bold',
            color: theme.text,
            ...ffs,
          }}
        >
          {t('hub.title')}
        </Text>
        <View style={styles.headerRight}>
          <CourseSwitcherButton size={iconSize} color={theme.primary} />
          <TouchableOpacity
            testID="hub-toggle-autoplay"
            accessibilityRole="switch"
            accessibilityLabel={t('profile.learning.autoplayTitle')}
            accessibilityState={{ checked: autoplay }}
            onPress={toggleAutoplay}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Ionicons
              name={autoplay ? 'volume-high' : 'volume-mute-outline'}
              size={iconSize}
              color={autoplay ? theme.primary : theme.textSecondary}
            />
          </TouchableOpacity>
          <TouchableOpacity
            testID="hub-button-history"
            accessibilityRole="button"
            accessibilityLabel={t('hub.historyButton')}
            onPress={handleHistory}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Ionicons name="time-outline" size={iconSize} color={theme.primary} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Content */}
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={[styles.scrollContent, { paddingBottom: paddingBottom + 16 }]}
      >
        <View style={styles.centered}>
          {/* Illustration Circle */}
          <View
            style={{
              width: circleSize,
              height: circleSize,
              borderRadius: circleSize / 2,
              backgroundColor: theme.primarySoft,
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: 16,
            }}
          >
            <Text
              allowFontScaling={false}
              accessible={false}
              importantForAccessibility="no"
              style={{ fontSize: BASE_CIRCLE_EMOJI * fontScale }}
            >
              🎓
            </Text>
          </View>

          {/* Subtitle */}
          <Text
            allowFontScaling={false}
            style={{
              fontSize: BASE_BODY_SIZE * fontScale,
              lineHeight: Math.ceil(BASE_BODY_LINE_HEIGHT * fontScale),
              color: theme.textSecondary,
              textAlign: 'center',
              marginBottom: 24,
              ...ffs,
            }}
          >
            {t('hub.subtitle')}
          </Text>

          {/* Loading indicator */}
          {isCreating && (
            <ActivityIndicator
              size="small"
              color={theme.primary}
              style={{ marginBottom: 12 }}
            />
          )}

          {/* Mode Grid — 5 cards in two rows: 2 on top, 3 on bottom */}
          <View style={styles.grid}>
            {[MODES.slice(0, 2), MODES.slice(2, 5)].map((row, rowIdx) => (
              <View key={rowIdx} style={styles.gridRow}>
                {row.map((config) => (
                  <ModeCard
                    key={config.mode}
                    config={config}
                    onPress={handleSelectMode}
                    disabled={isCreating}
                  />
                ))}
              </View>
            ))}
          </View>

          {/* Mastery progress entry — live counts, navigates to Vocabulary */}
          <View style={styles.progressRow}>
            <VocabProgressCard />
          </View>
        </View>
      </ScrollView>

      <AIConsentModal
        visible={showConsentModal}
        onAccept={handleConsentAccept}
        onDecline={handleConsentDecline}
      />

    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 8,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: 400,
    width: '100%',
    alignSelf: 'center',
  },
  grid: {
    width: '100%',
    gap: 12,
  },
  progressRow: {
    width: '100%',
    marginTop: 12,
  },
  gridRow: {
    flexDirection: 'row',
    gap: 12,
  },
  cardTouchable: {
    flex: 1,
  },
  card: {
    flex: 1,
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
  },
});
